import React from "react";

import Chip from "@mui/material/Chip";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import { PortfolioPlanResponse } from "@fartherfinance/frontend/api/Investment/types";

import { UNKNOWN_VALUE } from "../../../../../pages/Advisor/common";
import { typedKeys } from "@src/multiCustodian/utils/arrayHelpers";
import {
  Cell,
  Column,
  Row,
  TableSortOrder,
} from "@src/sharedComponents/Table/Types";

import styles from "./PortfolioPlan.module.css";

const TABLE_TITLE = {
  name: "Name",
  type: "Type",
  taxStatus: "Tax Status",
  assignmentCount: "Trading Groups Applied",
  createdAt: "Created",
  updatedAt: "Updated",
} as const;

export type TableKey = keyof typeof TABLE_TITLE;

export function isTableKey(str: string): str is TableKey {
  return Object.keys(TABLE_TITLE).includes(str);
}

export function isSortOrder(str: string): str is TableSortOrder {
  return str === "asc" || str === "desc";
}

export const tableColumnsCreator = (
  orderBy: [TableKey, TableSortOrder] | null
): Column<TableKey>[] =>
  typedKeys(TABLE_TITLE).map((column) => {
    const [orderName, order] = orderBy ?? [];

    return column === "createdAt" ||
      column === "updatedAt" ||
      column === "name" ||
      column === "assignmentCount"
      ? {
          label: TABLE_TITLE[column],
          accessor: column,
          sortable: true,
          active: orderName === column,
          sortOrder: order ?? "asc",
        }
      : {
          label: TABLE_TITLE[column],
          accessor: column,
          sortable: false,
        };
  });

const FartherChip = (props: { value: string }) => (
  <Chip
    classes={{
      root: styles.chip,
      label: styles.chipLabel,
      icon: styles.chipIcon,
    }}
    variant="outlined"
    label={props.value}
  />
);

type AbstractTags = PortfolioPlanResponse["data"][number]["plan"]["tags"];

const texts = {
  TaxExempt: "Tax Exempt",
  Taxable: "Taxable",
} as const;

type TaxStatus = "Tax Exempt" | "Taxable" | "Any";

export const whitelistTaxStatus = (AbstractTag?: AbstractTags): TaxStatus => {
  const [filteredTags] =
    AbstractTag?.filter((tag) => tag === "TaxExempt" || tag === "Taxable") ??
    [];

  return filteredTags !== undefined ? texts[filteredTags] : "Any";
};

export const tableRowDataCreator = (
  navigate: ReturnType<typeof useNavigate>,
  location: ReturnType<typeof useLocation>,
  portfolioRes?: PortfolioPlanResponse
): Row<TableKey, Cell>[] =>
  (portfolioRes?.data ?? []).map((row) => {
    return {
      key: row.plan.portfolioPlanId,
      name: row.plan.name,
      type: {
        value: row.plan.provider ? (
          <FartherChip value={row.plan.provider} />
        ) : (
          UNKNOWN_VALUE
        ),
      },
      taxStatus: {
        value: <FartherChip value={whitelistTaxStatus(row.plan.tags)} />,
      },
      assignmentCount: row.plan.assignmentCount?.toString() ?? UNKNOWN_VALUE,
      createdAt: {
        value: format(row.plan.createdAt, "M/d/yyyy"),
        fullValue: row.plan.createdAt.toISOString(),
      },
      updatedAt: {
        value: format(row.plan.updatedAt, "M/d/yyyy"),
        fullValue: row.plan.updatedAt.toISOString(),
      },
      onClick: () => {
        navigate({
          ...location,
          pathname: `/Advisor/Investments/ModelMarketplaceV2/${row.plan.portfolioPlanId}/ModelDetails`,
        });
      },
    };
  });
